import groupBy from 'lodash/groupBy'
import { sum } from '../../helpers/array';
import { ItemWorkareaData, ViewItem } from './itemModel';
import { CatalogItem } from '../../domain/catalogItem';

export type ItemGrouping = {
  key: string
  name: string
  estimatedQuantity: number
  actualQuantity: number
  units: string
  estimatedTotalCost: number
  actualTotalCost: number
  difference: number
  totalActualPrice: number
  actualItems: ViewItem[]
  catalogItemGuid: string
}

export const defaultItemGrouping = (): ItemGrouping => ({
  key: '',
  name: '',
  estimatedQuantity: 0,
  actualQuantity: 0,
  units: '',
  estimatedTotalCost: 0,
  actualTotalCost: 0,
  difference: 0,
  totalActualPrice: 0,
  actualItems: [],
  catalogItemGuid: ''
})

export const getGroupName = (item: Pick<ViewItem, 'guid' | 'catalogItemGuid' | 'isFromTimesheets' | 'name'>, catalogItems: CatalogItem[]): string => {
  return item.isFromTimesheets
    ? catalogItems.find(x => x.guid === item.catalogItemGuid)?.name ?? 'Labor'
    : item.name
}

export const groupKey = (item: ViewItem, catalogItems: CatalogItem[]): string => {
  return `${item.catalogItemGuid}-${getGroupName(item, catalogItems)}`
}

export const groupItems = (estimatedItems: ViewItem[], actualItems: ViewItem[], catalogItems: CatalogItem[], workareas: ItemWorkareaData[]): ItemGrouping[] => {
  const estimatedItemsMultipliedByOccurrences = estimatedItems.map(x => {
    const workarea = workareas.find(w => w.id === x.workareaId)
    const estimatedOccurrences = workarea?.estimatedOccurrences ?? 1
    return { ...x, quantity: x.quantity * estimatedOccurrences }
  })
  const groupedEstimates = groupBy(estimatedItemsMultipliedByOccurrences, x => groupKey(x, catalogItems))
  const groupedActuals = groupBy(actualItems, x => groupKey(x, catalogItems))

  const distinctKeys = new Set<string>()
  Object.keys(groupedEstimates).forEach(x => distinctKeys.add(x))
  Object.keys(groupedActuals).forEach(x => distinctKeys.add(x))

  return Array.from(distinctKeys).map(key => {
    const estimates = groupedEstimates[key] ?? []
    const actuals = groupedActuals[key] ?? []
    const first = actuals[0] ?? estimates[0]
    const estimatedTotalCost = sum(estimates, x => x.costPlusTax * (x.isDeleted ? 0 : x.quantity))
    const actualTotalCost = sum(actuals, x => x.costPlusTax * (x.isDeleted ? 0 : x.quantity))
    return {
      key,
      name: getGroupName(first, catalogItems),
      estimatedQuantity: sum(estimates, x => x.isDeleted ? 0 : x.quantity),
      actualQuantity: sum(actuals, x => x.isDeleted ? 0 : x.quantity),
      units: first.units,
      estimatedTotalCost,
      actualTotalCost,
      difference: estimatedTotalCost - actualTotalCost,
      totalActualPrice: sum(actuals, x => x.isDeleted ? 0 : x.totalPrice),
      actualItems: actuals,
      catalogItemGuid: first.catalogItemGuid
    }
  })
}
